import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import { distance } from "ol/coordinate";
import { Extent } from "ol/extent";
import Feature, { FeatureLike } from "ol/Feature";
import Geometry from "ol/geom/Geometry";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import { useAccess } from "~/utils/hooks/useAccess";
import { findLayerByTitle } from "../functions/findLayerByTitle";
import {
  readMapSettings,
  updateMapSettings,
} from "../functions/localMapSettings";
import { LayerDefaults, LayerTitle } from "../types/layers";
import { CustomMapEventTypes, MapDataTitle } from "../types/map";

const useStyles = makeStyles((theme) => ({
  accordion: {
    backgroundColor: theme.palette.primary.semiLight,
    color: theme.palette.primary.contrastText,
    width: "100%",
  },
  accordionIcon: {
    color: theme.palette.primary.contrastText,
  },
  accordionHeading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  },
  accordionSummaryContent: {
    alignItems: "center",
  },
  visibilityButton: {
    marginLeft: theme.spacing(1),
  },
  visibilityIcon: {
    fill: theme.palette.primary.contrastText,
  },
}));

interface ListProps<T> {
  layerTitle: LayerTitle;
  map: Map;
  mapExtent?: Extent;
  showInViewOnly: boolean;
  render: (data: T, style: React.CSSProperties) => JSX.Element;
}

interface VisibilityProps {
  visibilityToggle?: boolean;
}

interface AccordionProps {
  title: MapDataTitle;
  defaultOpen?: boolean;
}

const MapDataList = <T,>({
  layerTitle,
  map,
  mapExtent,
  showInViewOnly,
  render,
  title,
  defaultOpen,
  visibilityToggle,
}: ListProps<T> & AccordionProps & VisibilityProps): JSX.Element | null => {
  const classes = useStyles();
  const [dataToRender, setDataToRender] = useState<T[]>([]);
  const [featuresInExtent, setFeaturesInExtent] = useState<Feature<Geometry>[]>(
    [],
  );
  const [allFeatures, setAllFeatures] = useState<Feature<Geometry>[]>([]);
  const [expanded, setExpanded] = useState(
    readMapSettings("accordions", title)?.open ?? defaultOpen,
  );
  const [currentVisibility, setCurrentVisibility] = useState<boolean>();
  const [updateData, setUpdateData] = useState(false);
  const { accesses } = useAccess();

  const { t } = useTranslation("map");

  const hasAccess = React.useMemo(
    () =>
      Boolean(
        !LayerDefaults[layerTitle].requiredAccess ||
          accesses[LayerDefaults[layerTitle].requiredAccess!],
      ),
    [accesses, layerTitle],
  );

  // Event listener for layer source changes and for settings change
  useEffect(() => {
    let source: VectorSource<Geometry> | undefined;
    const handleSourceChange = () => {
      setUpdateData((prev) => !prev);
    };
    if (hasAccess) {
      const layer = findLayerByTitle(map, layerTitle);
      if (layer) {
        source = (layer as VectorLayer).getSource();

        if (!source.getListeners(CustomMapEventTypes.FeaturesAdded)) {
          source.on(CustomMapEventTypes.FeaturesAdded, handleSourceChange);
        }
        source.on(CustomMapEventTypes.SettingsChanged, handleSourceChange);
      }
    }
    return () => {
      source?.un(CustomMapEventTypes.FeaturesAdded, handleSourceChange);
      source?.un(CustomMapEventTypes.SettingsChanged, handleSourceChange);
    };
  }, [map, layerTitle, hasAccess]);

  // Get layer visibility
  useEffect(() => {
    if (!hasAccess) return;
    const layer = findLayerByTitle(map, layerTitle);
    setCurrentVisibility(layer?.getVisible());
  }, [hasAccess, layerTitle, map]);

  // Features in current map extent
  useEffect(() => {
    if (!showInViewOnly || !mapExtent || !hasAccess) return;
    const layer = findLayerByTitle(map, layerTitle);
    if (!layer || !(layer instanceof VectorLayer)) return;
    const source = layer.getSource();
    setFeaturesInExtent(source.getFeaturesInExtent(mapExtent));
  }, [map, mapExtent, showInViewOnly, layerTitle, updateData, hasAccess]);

  // All features
  useEffect(() => {
    if (showInViewOnly || !hasAccess) return;
    const layer = findLayerByTitle(map, layerTitle);
    if (!layer || !(layer instanceof VectorLayer)) return;
    const source = layer.getSource();
    setAllFeatures(source.getFeatures());
  }, [map, showInViewOnly, layerTitle, updateData, hasAccess]);

  // Find the correct layer and display the data
  useEffect(() => {
    if (!hasAccess) return;
    const data: T[] = [];
    const features = showInViewOnly ? featuresInExtent : allFeatures;
    // Sort features by distance from center of the screen
    const center = map.getView().getCenter();
    const sortedFeatures = center
      ? features.sort((a, b) => {
          const aClosest = a.getGeometry()?.getClosestPoint(center);
          const bClosest = b.getGeometry()?.getClosestPoint(center);
          if (!aClosest || !bClosest) return 0;
          const aDistance = Math.abs(distance(center, aClosest));
          const bDistance = Math.abs(distance(center, bClosest));
          return aDistance - bDistance;
        })
      : features;
    sortedFeatures.forEach((f) => {
      const clustered = f.getProperties().features as FeatureLike[];
      if (clustered) {
        data.push(
          ...clustered.map((c) => ({
            ...(c.getProperties() as T),
            mapId: c.getId(),
          })),
        );
      } else {
        data.push({ ...(f.getProperties() as T), mapId: f.getId() });
      }
    });
    setDataToRender(data);
  }, [allFeatures, featuresInExtent, showInViewOnly, map, hasAccess]);

  const renderRow = useCallback(
    (props: ListChildComponentProps) => {
      if (!hasAccess) return null;
      const { style, index } = props;
      return render(dataToRender[index], style);
    },
    [dataToRender, hasAccess, render],
  );

  const handleVisibilityToggle = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    const layer = findLayerByTitle(map, layerTitle);
    const curVisible = layer?.getVisible();
    layer?.setVisible(!curVisible);
    setCurrentVisibility(!curVisible);
    map.dispatchEvent(CustomMapEventTypes.SettingsChanged);
  };

  if (!hasAccess) return null;

  return (
    <ListItem>
      <Accordion
        className={classes.accordion}
        expanded={expanded}
        onChange={(_, open) => {
          setExpanded(open);
          updateMapSettings({ accordions: { title, open } });
        }}
      >
        <AccordionSummary
          classes={{
            expandIconWrapper: classes.accordionIcon,
            content: classes.accordionSummaryContent,
          }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Typography className={classes.accordionHeading}>
            {t(title)}
          </Typography>
          {visibilityToggle && (
            <Tooltip
              title={
                currentVisibility
                  ? t("hide-layer").toString()
                  : t("show-layer").toString()
              }
            >
              <IconButton
                aria-label="Toggle layer visibility"
                onClick={handleVisibilityToggle}
                className={classes.visibilityButton}
                onFocus={(event) => event.stopPropagation()}
                size="large"
              >
                {currentVisibility ? (
                  <VisibilityOffIcon className={classes.visibilityIcon} />
                ) : (
                  <VisibilityIcon className={classes.visibilityIcon} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <FixedSizeList
            height={300}
            width="100%"
            itemSize={48}
            itemCount={dataToRender.length}
          >
            {renderRow}
          </FixedSizeList>
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
};

export default MapDataList;
